/* @flow */
import Vue from "vue";
import Router from "vue-router";
import equal from "fast-deep-equal";
import delay from "delay";
import oPath from "object-path";
import { isPast } from "date-fns";

import store, {SET_UNSAVED_CHANGES_FALSE} from '@/store'
import {REMOVE_SESSION} from '@/store/session'

import ApplicationNetworkSpeedDial from '@/components/ApplicationNetworkSpeedDial'
import GatewaySpeedDial from '@/components/GatewaySpeedDial'
import EndDeviceSpeedDial from '@/components/EndDeviceSpeedDial'
import ServiceApiKeySpeedDial from '@/components/service_api_keys/ServiceApiKeySpeedDial'
import DeviceGroupSpeedDial from '@/components/DeviceGroupSpeedDial'
import DeviceProfileSpeedDial from '@/components/DeviceProfileSpeedDial'
import NetworkProfileSpeedDial from '@/components/NetworkProfileSpeedDial'
import UserSpeedDial from '@/components/UserSpeedDial'
import OrganizationSpeedDial from '@/components/OrganizationSpeedDial'
import PolicySpeedDial from '@/components/PolicySpeedDial'

import OfflinePluginRuntime from "offline-plugin/runtime";

const App = () => import(/* webpackChunkName: "App" */ "@/App");
const Login = () => import(/* webpackChunkName: "Login" */ "@/screens/Login");
const Dashboard = () =>
  import(/* webpackChunkName: "Dashboard" */ "@/screens/Dashboard");
const MapView = () =>
  import(/* webpackChunkName: "MapView" */ "@/screens/MapView");
const PacketsChart = () =>
  import(/* webpackChunkName: "PacketsChart" */ "@/components/PacketsChart");
const JoinRequestsChart = () =>
  import(
    /* webpackChunkName: "JoinRequestsChart" */ "@/components/JoinRequestsChart"
  );
const CrcPercentageChart = () =>
  import(
    /* webpackChunkName: "CrcPercentageChart" */ "@/components/CrcPercentageChart"
  );
const MissedPacketsChart = () =>
  import(
    /* webpackChunkName: "MissedPacketsChart" */ "@/components/MissedPacketsChart"
  );
const UnlockUser = () =>
  import(/* webpackChunkName: "UnlockUser" */ "@/screens/UnlockUser");
const ApplicationNetworksList = () =>
  import(
    /* webpackChunkName: "ApplicationNetworksList" */ "@/components/ApplicationNetworksList"
  );
const ApplicationNetworks = () =>
  import(
    /* webpackChunkName: "ApplicationNetworks" */ "@/screens/ApplicationNetworks"
  );
const ApplicationNetwork = () =>
  import(
    /* webpackChunkName: "ApplicationNetwork" */ "@/screens/ApplicationNetwork"
  );
const ApplicationNetworkDashboard = () =>
  import(
    /* webpackChunkName: "ApplicationNetworkDashboard" */ "@/components/ApplicationNetworkDashboard"
  );
const NewApplicationNetworkModal = () =>
  import(
    /* webpackChunkName: "NewApplicationNetworkModal" */ "@/components/NewApplicationNetworkModal"
  );
const Gateways = () =>
  import(/* webpackChunkName: "Gateways" */ "@/screens/Gateways");
const GatewaysTable = () =>
  import(/* webpackChunkName: "GatewaysTable" */ "@/components/GatewaysTable");
const Gateway = () =>
  import(/* webpackChunkName: "Gateway" */ "@/screens/Gateway");
const GatewayDashboard = () =>
  import(
    /* webpackChunkName: "GatewayDashboard" */ "@/components/GatewayDashboard"
  );
const NewGatewayModal = () =>
  import(
    /* webpackChunkName: "NewGatewayModal" */ "@/components/NewGatewayModal"
  );
const UploadGatewayModal = () =>
  import(
    /* webpackChunkName: "UploadGatewayModal" */ "@/components/UploadGatewayModal"
  );
const GatewayDetails = () =>
  import(
    /* webpackChunkName: "GatewayDetails" */ "@/components/GatewayDetails"
  );
const GatewayRevisions = () =>
  import(
    /* webpackChunkName: "GatewayRevisions" */ "@/modules/gatewayStates/views/GatewayRevisions"
  );
const AppAuth = () =>
  import(
    /* webpackChunkName: "GatewayRevisions" */ "@/modules/applicationNetworkStates/views/AppAuth"
  );
const RssiSpectralScan = () =>
  import(
    /* webpackChunkName: "RssiSpectralScan" */ "@/modules/gatewayStates/views/RssiSpectralScan"
  );
const GatewayLicense = () =>
  import(
    /* webpackChunkName: "GatewayLicense" */ "@/modules/gatewayStates/views/GatewayLicense"
  );
const ApplicationNetworkRevisions = () =>
  import(
    /* webpackChunkName: "ApplicationNetworkRevisions" */ "@/modules/applicationNetworkStates/views/ApplicationNetworkRevisions"
  );
const EndDeviceRevisions = () =>
  import(
    /* webpackChunkName: "EndDeviceRevisions" */ "@/modules/endDeviceStates/views/EndDeviceRevisions"
  );
const DeviceProfileRevisions = () =>
  import(
    /* webpackChunkName: "DeviceProfileRevisions" */ "@/modules/endDeviceStates/views/DeviceProfileRevisions"
  );
const NetworkProfileRevisions = () =>
  import(
    /* webpackChunkName: "NetworkProfileRevisions" */ "@/modules/gatewayStates/views/NetworkProfileRevisions"
  );
const OrganizationActivity = () =>
  import(
    /* webpackChunkName: "OrganizationActivity" */ "@/modules/organizationStates/views/OrganizationActivity"
  );
const OrganizationSessions = () =>
  import(
    /* webpackChunkName: "OrganizationSessions" */ "@/modules/organizationStates/views/OrganizationSessions"
  );
const OrganizationRevisions = () =>
  import(
    /* webpackChunkName: "OrganizationRevisions" */ "@/modules/organizationStates/views/OrganizationRevisions"
  );
const OrganizationCreateRevisions = () =>
  import(
    /* webpackChunkName: "OrganizationCreateRevisions" */ "@/modules/organizationStates/views/OrganizationCreateRevisions"
  );
const OrganizationUpdateRevisions = () =>
  import(
    /* webpackChunkName: "OrganizationUpdateRevisions" */ "@/modules/organizationStates/views/OrganizationUpdateRevisions"
  );
const OrganizationDestroyRevisions = () =>
  import(
    /* webpackChunkName: "OrganizationDestroyRevisions" */ "@/modules/organizationStates/views/OrganizationDestroyRevisions"
  );
const OrganizationBroadcast = () =>
  import(
    /* webpackChunkName: "OrganizationBroadcast" */ "@/modules/organizationStates/views/OrganizationBroadcast"
  );
const OrganizationLicense = () =>
  import(
    /* webpackChunLicense" */ "@/modules/organizationStates/views/OrganizationLicense"
  );
const EulaAccept = () =>
  import(
    /* webpackChunkName: "EulaAccept" */ "@/modules/organizationStates/views/EulaAccept"
  );
const OrganizationDashboard = () =>
  import(
    /* webpackChunkName: "OrganizationDashboard" */ "@/modules/organizationStates/views/OrganizationDashboard"
  );
const OrganizationSummary = () =>
  import(
    /* webpackChunkName: "OrganizationSummary" */ "@/modules/organizationStates/views/OrganizationSummary"
  );
const OrganizationAssignTokens = () =>
  import(
    /* webpackChunkName: "OrganizationAssignTokens" */ "@/modules/organizationStates/views/OrganizationAssignTokens"
  );
const OrganizationScheduleDeviceProvisioning = () =>
  import(
    /* webpackChunkName: "OrganizationAssignTokens" */ "@/modules/organizationStates/views/OrganizationScheduleDeviceProvisioning"
  );
const OrganizationJoinChart = () =>
  import(
    /* webpackChunkName: "OrganizationJoinChart" */ "@/modules/organizationStates/views/OrganizationJoinChart"
  );
const OrganizationPacketsChart = () =>
  import(
    /* webpackChunkName: "OrganizationPacketsChart" */ "@/modules/organizationStates/views/OrganizationPacketsChart"
  );
const OrganizationCrcErrorsChart = () =>
  import(
    /* webpackChunkName: "OrganizationCrcErrorsChart" */ "@/modules/organizationStates/views/OrganizationCrcErrorsChart"
  );
const OrganizationMissedPacketsChart = () =>
  import(
    /* webpackChunkName: "OrganizationMissedPacketsChart" */ "@/modules/organizationStates/views/OrganizationMissedPacketsChart"
  );
const JoinRequests = () =>
  import(
    /* webpackChunkName: "JoinRequests" */ "@/modules/organizationStates/views/JoinRequests"
  );
const SimpleJoinRequests = () =>
  import(
    /* webpackChunkName: "SimpleJoinRequests" */ "@/modules/organizationStates/views/SimpleJoinRequests"
  );
const Packets = () =>
  import(
    /* webpackChunkName: "Packets" */ "@/modules/organizationStates/views/Packets"
  );
const NetworkStatistics = () =>
  import(
    /* webpackChunkName: "NetworkStatistics" */ "@/modules/organizationStates/views/NetworkStatistics"
  );
const GatewayStatistics = () =>
  import(
    /* webpackChunkName: "GatewayStatistics" */ "@/modules/organizationStates/views/GatewayStatistics"
  );
const UserActions = () =>
  import(
    /* webpackChunkName: "UserActions" */ "@/modules/organizationStates/views/UserActions"
  );
const UserRevisions = () =>

  import(/* webpackChunkName: "UserRevisions" */ '@/modules/organizationStates/views/UserRevisions')

const EndDevices = () =>
  import(/* webpackChunkName: "EndDevices" */ '@/screens/EndDevices')

const ServiceApiKeys = () =>
  import(/* webpackChunkName: "ServiceApiKeys" */ '@/screens/ServiceApiKeys')

const EndDevicesTable = () =>
  import(/* webpackChunkName: "EndDevicesTable" */ '@/components/EndDevicesTable')
const EndDevice = () => import(/* webpackChunkName: "EndDevice" */ '@/screens/EndDevice')

const ServiceApiKeysTable = () =>
  import(/* webpackChunkName: "ServiceApiKeysTable" */ '@/components/service_api_keys/ServiceApiKeysTable')
const ServiceApiKey = () => import(/* webpackChunkName: "ServiceApiKey" */ '@/screens/ServiceApiKey')

const NetworkProfiles = () =>
  import(/* webpackChunkName: "NetworkProfiles" */ "@/screens/NetworkProfiles");
const NetworkProfile = () =>
  import(/* webpackChunkName: "NetworkProfile" */ "@/screens/NetworkProfile");
const NewNetworkProfileModal = () =>
  import(
    /* webpackChunkName: "NewNetworkProfileModal" */ "@/components/NewNetworkProfileModal"
  );
const EndDeviceDashboard = () =>
  import(
    /* webpackChunkName: "EndDeviceDashboard" */ "@/components/EndDeviceDashboard"
  );
const NewEndDeviceModal = () =>
  import(/* webpackChunkName: "NewEndDeviceModal" */ '@/components/NewEndDeviceModal')

const ServiceApiKeyDashboard = () =>
  import(/* webpackChunkName: "ServiceApiKeyDashboard" */ '@/components/service_api_keys/ServiceApiKeyDashboard')
const NewServiceApiKeyModal = () =>
  import(/* webpackChunkName: "NewServiceApiKeyModal" */ '@/components/service_api_keys/NewServiceApiKeyModal')

const UploadEndDeviceModal = () =>
  import(
    /* webpackChunkName: "UploadEndDeviceModal" */ "@/components/UploadEndDeviceModal"
  );
const OrganizationDeviceProfiles = () =>
  import(
    /* webpackChunkName: "OrganizationDeviceProfiles" */ "@/components/OrganizationDeviceProfiles"
  );
const DeviceProfiles = () =>
  import(/* webpackChunkName: "DeviceProfiles" */ "@/screens/DeviceProfiles");
const DeviceProfile = () =>
  import(/* webpackChunkName: "DeviceProfile" */ "@/screens/DeviceProfile");
const DeviceProfileModal = () =>
  import(
    /* webpackChunkName: "DeviceProfileModal" */ "@/components/DeviceProfileModal"
  );
const Operations = () =>
  import(/* webpackChunkName: "Operations" */ "@/screens/Operations");
const DeviceGroups = () =>
  import(/* webpackChunkName: "DeviceGroups" */ "@/screens/DeviceGroups");
const DeviceGroup = () =>
  import(/* webpackChunkName: "DeviceGroup" */ "@/screens/DeviceGroup");
const NewDeviceGroupModal = () =>
  import(
    /* webpackChunkName: "NewDeviceGroupModal" */ "@/components/NewDeviceGroupModal"
  );
const Policies = () =>
  import(/* webpackChunkName: "Policies" */ "@/screens/Policies");
const Policy = () =>
  import(/* webpackChunkName: "Policy" */ "@/screens/Policy");
const NewPolicyModal = () =>
  import(
    /* webpackChunkName: "NewPolicyModal" */ "@/components/NewPolicyModal"
  );
const NewOperation = () =>
  import(/* webpackChunkName: "NewOperation" */ "@/screens/NewOperation");
const PacketsTable = () =>
  import(/* webpackChunkName: "PacketsTable" */ "@/components/PacketsTable");
const JoinRequestsTable = () =>
  import(
    /* webpackChunkName: "JoinRequestsTable" */ "@/components/JoinRequestsTable"
  );
const GatewayStatisticsTable = () =>
  import(
    /* webpackChunkName: "GatewayStatisticsTable" */ "@/components/GatewayStatisticsTable"
  );
const NetworkStatisticsTable = () =>
  import(
    /* webpackChunkName: "NetworkStatisticsTable" */ "@/components/NetworkStatisticsTable"
  );
const Users = () => import(/* webpackChunkName: "Users" */ "@/screens/Users");
const UserModal = () =>
  import(/* webpackChunkName: "UserModal" */ "@/components/UserModal");
const Me = () => import(/* webpackChunkName: "Me" */ "@/screens/Me");
const Organizations = () =>
  import(/* webpackChunkName: "Organizations" */ "@/screens/Organizations");
const Organization = () =>
  import(/* webpackChunkName: "Organization" */ "@/screens/Organization");
const SystemOrganization = () =>
  import(/* webpackChunkName: "Organization" */ "@/screens/SystemOrganization");
const OrganizationModal = () =>
  import(
    /* webpackChunkName: "OrganizationModal" */ "@/components/OrganizationModal"
  );
const Support = () =>
  import(/* webpackChunkName: "Support" */ "@/screens/Support");
const SystemLimits = () =>
  import(/* webpackChunkName: "SystemLimits" */ "@/screens/SystemLimits");
const Vaults = () =>
  import(/* webpackChunkName: "Vaults" */ "@/screens/Vaults");
const VaultCreateController = () =>
  import(
    /* webpackChunkName: "VaultCreateController" */ "@/modules/systemStates/views/VaultCreateController"
  );
const VaultUpdateController = () =>
  import(
    /* webpackChunkName: "VaultUpdateController" */ "@/modules/systemStates/views/VaultUpdateController"
  );
const VaultTransactions = () =>
  import(
    /* webpackChunkName: "VaultTransactions" */ "@/modules/systemStates/components/VaultTransactions"
  );

const LensSiteCreateView = () =>
  import(
    /* webpackChunkName: "LensSiteCreateView" */ "@/modules/systemStates/components/LensSiteCreateView"
  );
const LensSiteUpdateView = () =>
  import(
    /* webpackChunkName: "LensSiteUpdateView" */ "@/modules/systemStates/components/LensSiteUpdateView"
  );
const TenantAccountCreateView = () =>
  import(
    /* webpackChunkName: "TenantAccountCreateView" */ "@/modules/systemStates/components/TenantAccountCreateView"
  );
const TenantAccountUpdateView = () =>
  import(
    /* webpackChunkName: "TenantAccountUpdateView" */ "@/modules/systemStates/components/TenantAccountUpdateView"
  );

const SystemBroadcast = () =>
  import(/* webpackChunkName: "SystemBroadcast" */ "@/screens/SystemBroadcast");

// DeviceHQ Views and Components
const DeviceHQLogin = () =>
  import(/* webpackChunkdName: "DeviceHQLogin" */ "@/devicehq/views/Login");
const DeviceHQAccount = () =>
  import(/* webpackChunkdName: "DeviceHQAccount" */ "@/devicehq/views/Account");
const DeviceHQUsers = () =>
  import(/* webpackChunkdName: "DeviceHQUsers" */ "@/devicehq/views/Users");
const DeviceHQDevices = () =>
  import(/* webpackChunkdName: "DeviceHQDevices" */ "@/devicehq/views/Devices");
const DeviceHQDevice = () =>
  import(/* webpackChunkdName: "DeviceHQDevice" */ "@/devicehq/views/Device");

const ApplicationNetworkStates = () =>
  import(
    /* webpackChunkdName: "ApplicationNetworkStates" */ "@/modules/applicationNetworkStates"
  );
const EndDeviceStates = () =>
  import(
    /* webpackChunkdName: "EndDeviceStates" */ "@/modules/endDeviceStates"
  );
const HealthCheckManagement = () =>
  import(
    /* webpackChunkdName: "HealthCheckManagement" */ "@/modules/endDeviceStates/views/HealthCheckManagement"
  );
const GatewayStates = () =>
  import(/* webpackChunkdName: "GatewayStates" */ "@/modules/gatewayStates");
const AppNetDeviceStates = () =>
  import(
    /* webpackChunkdName: "AppNetDeviceStates" */ "@/modules/applicationNetworkStates/views/DeviceStates"
  );
const GatewayDeviceStates = () =>
  import(
    /* webpackChunkdName: "GatewayDeviceStates" */ "@/modules/gatewayStates/views/DeviceStates"
  );
const Operation = () =>
  import(
    /* webpackChunkdName: "Operation" */ "@/modules/endDeviceStates/views/Operation"
  );

Vue.use(Router);

const routes = [
  {
    path: "/login",
    alias: ["/reset-password", "/set-password"],
    name: Login.name,
    component: Login,
    meta: { noAuth: true },
  },
  {
    path: "/logout",
    beforeEnter: (to, from, next) => {
      store.commit(REMOVE_SESSION);
      next("/login");
    },
    meta: { noAuth: true },
  },
  {
    path: "/unlock-user",
    name: "UnlockUser",
    component: UnlockUser,
    meta: { noAuth: true },
  },
  {
    path: "/",
    component: App,
    children: [
      {
        path: "eula",
        name: "EulaAccept",
        component: EulaAccept,
      },
      {
        path: "/devicehq/login",
        // alias: ['/devicehq/reset-password', 'devicehq/set-password'],
        name: DeviceHQLogin.name,
        component: DeviceHQLogin,
        meta: { noAuth: true },
      },
      {
        path: "devicehq/account",
        name: DeviceHQAccount.name,
        component: DeviceHQAccount,
      },
      {
        path: "devicehq/users",
        name: DeviceHQUsers.name,
        component: DeviceHQUsers,
      },
      {
        path: "devicehq/devices",
        name: DeviceHQDevices.name,
        component: DeviceHQDevices,
      },
      {
        path: "devicehq/devices/:uuid",
        name: DeviceHQDevice.name,
        component: DeviceHQDevice,
      },
      {
        path: "application-network-states",
        name: ApplicationNetworkStates.name,
        component: ApplicationNetworkStates,
      },
      {
        path: "end-device-states",
        name: EndDeviceStates.name,
        component: EndDeviceStates,
      },
      {
        path: "health-check-management",
        name: "Health Check Management",
        component: HealthCheckManagement,
      },
      {
        path: "gateway-states",
        name: GatewayStates.name,
        component: GatewayStates,
      },
      {
        path: "application-network-states/device-states/:id",
        name: AppNetDeviceStates.name,
        component: AppNetDeviceStates,
        props: true,
      },
      {
        path: "gateway-states/device-states/:id",
        name: GatewayDeviceStates.name,
        component: GatewayDeviceStates,
        props: true,
      },
      {
        path: "",
        redirect: "dashboard",
      },
      {
        path: "dashboard",
        name: Dashboard.name,
        component: Dashboard,
        children: [
          {
            path: "",
            redirect: "map_view",
          },
          {
            path: "map_view",
            name: "MapView",
            component: MapView,
          },
          {
            path: "packets_chart",
            name: "PacketsChart",
            component: PacketsChart,
          },
          {
            path: "join_requests_chart",
            name: "JoinRequestsChart",
            component: JoinRequestsChart,
          },
          {
            path: "crc_percentage_chart",
            name: "CrcPercentageChart",
            component: CrcPercentageChart,
          },
          {
            path: "missed_packets_chart",
            name: "MissedPacketsChart",
            component: MissedPacketsChart,
          },
        ],
      },
      {
        path: "application-networks",
        name: ApplicationNetworks.name,
        components: {
          default: ApplicationNetworks,
          speeddial: ApplicationNetworkSpeedDial,
        },
        props: {
          speeddial: (route) => ({
            routeName: route.name,
            routeKey: route.path.split("/")[1],
          }),
        },
        children: [
          {
            path: "new",
            name: "ApplicationNetworksNew",
            component: NewApplicationNetworkModal,
          },
        ],
      },
      {
        path: "application-networks/profiles",
        name: NetworkProfiles.name,
        components: {
          default: NetworkProfiles,
          speeddial: NetworkProfileSpeedDial,
        },
        props: {
          speeddial: (route) => ({
            routeName: route.name,
            routeKey: route.path.split("/")[1],
          }),
        },
        children: [
          {
            path: "new",
            name: "NewNetworkProfileModal",
            component: NewNetworkProfileModal,
          },
        ],
      },
      {
        path: "application-networks/profiles/:id",
        components: {
          default: NetworkProfile,
          //   speeddial: NetworkProfileSpeedDial,  FIXME: The SpeedDial does not allow NewNetworkProfileModal to work correctly
        },
        props: {
          default: true,
        },
        children: [
          {
            path: "(.*)?new",
            name: "NetworkProfileNew",
            components: { modal: NewNetworkProfileModal },
          },
          {
            path: "",
            redirect: "revisions",
          },
          {
            path: "revisions",
            name: "NetworkProfileRevisions",
            component: NetworkProfileRevisions,
            props: true,
          },
        ],
      },
      {
        path: "application-networks/:eui",
        components: {
          default: ApplicationNetwork,
          speeddial: ApplicationNetworkSpeedDial,
        },
        props: {
          default: true,
        },
        children: [
          {
            path: "",
            redirect: "dashboard",
          },
          {
            path: "dashboard",
            name: "ApplicationNetworkDashboard",
            component: ApplicationNetworkDashboard,
            props: true,
          },
          {
            path: "join-requests",
            name: "ApplicationNetworkJoinRequests",
            component: JoinRequestsTable,
            props: (route) => ({
              appEui: route.params.eui,
              hidden: ["app_eui"],
            }),
          },
          {
            path: "packets",
            name: "ApplicationNetworkPackets",
            component: PacketsTable,
            props: (route) => ({
              appEui: route.params.eui,
              uplink: true,
              downlink: true,
              hidden: ["app_eui"],
            }),
          },
          {
            path: "device-states",
            name: "ApplicationNetworkDeviceStates",
            component: AppNetDeviceStates,
          },
          {
            path: "app_auth",
            name: "AppAuth",
            component: AppAuth,
            props: true,
          },
          {
            path: "revisions",
            name: "ApplicationNetworkRevisions",
            component: ApplicationNetworkRevisions,
            props: true,
          },
          {
            path: "gateways",
            name: "ApplicationNetworkGateways",
            component: GatewaysTable,
            props: (route) => ({ appEui: route.params.eui }),
          },
          {
            path: "end-devices",
            name: "ApplicationNetworkEndDevices",
            component: EndDevicesTable,
            props: (route) => ({
              appEui: route.params.eui,
              hidden: ["application_network_id"],
            }),
          },
          {
            path: "(.*)?new",
            name: "ApplicationNetworkNew",
            components: { modal: NewApplicationNetworkModal },
          },
        ],
      },
      {
        path: "organization/:id",
        name: "OrganizationActivity",
        component: OrganizationActivity,
        props: true,
        children: [
          {
            path: "",
            redirect: "sessions",
          },
          {
            path: "sessions",
            name: "OrganizationSessions",
            component: OrganizationSessions,
            props: true,
          },
          {
            path: "create_revisions",
            name: "OrganizationCreateRevisions",
            component: OrganizationCreateRevisions,
            props: true,
          },
          {
            path: "update_revisions",
            name: "OrganizationUpdateRevisions",
            component: OrganizationUpdateRevisions,
            props: true,
          },
          {
            path: "destroy_revisions",
            name: "OrganizationDestroyRevisions",
            component: OrganizationDestroyRevisions,
            props: true,
          },
        ],
      },
      {
        path: "organization/:id/license",
        name: "OrganizationLicense",
        component: OrganizationLicense,
        props: true,
      },
      {
        path: "organization/:id/broadcasts",
        name: "OrganizationBroadcast",
        component: OrganizationBroadcast,
        props: true,
      },
      {
        path: "my_organization/:id",
        name: Organization.name,
        component: Organization,
        props: true,
        children: [
          {
            path: "",
            redirect: "revisions",
          },
          // {
          //   path: 'dashboard',
          //   name: 'OrganizationDashboard',
          //   component: OrganizationDashboard,
          //   props: route => ({
          //     orgId: route.params.id,
          //   })
          // },
          // {
          //   path: 'join_requests',
          //   name: 'OrganizationJoinRequests',
          //   component: JoinRequests,
          //   props: route => ({
          //     orgId: route.params.id,
          //   })
          // },
          // {
          //   path: 'packets',
          //   name: 'OrganizationPackets',
          //   component: Packets,
          //   props: route => ({
          //     orgId: route.params.id,
          //   })
          // },
          // {
          //   path: 'network_statistics',
          //   name: 'OrganizationNetworkStatistics',
          //   component: NetworkStatistics,
          //   props: route => ({
          //     orgId: route.params.id,
          //   })
          // },
          // {
          //   path: 'gateway_statistics',
          //   name: 'OrganizationGatewayStatistics',
          //   component: GatewayStatistics,
          //   props: route => ({
          //     orgId: route.params.id,
          //   })
          // },
          {
            path: "revisions",
            name: "OrganizationRevisions",
            component: OrganizationRevisions,
            props: true,
          },
        ],
      },
      {
        path: "organizations",
        name: Organization.name,
        components: {
          default: Organizations,
          speeddial: OrganizationSpeedDial,
        },
        props: {
          speeddial: (route) => ({
            routeName: route.name,
            routeKey: route.path.split("/")[1],
          }),
        },
        children: [
          {
            path: "new",
            name: "OrganizationsNew",
            component: OrganizationModal,
          },
        ],
      },
      {
        path: "organizations/:id",
        components: {
          default: SystemOrganization,
          speeddial: OrganizationSpeedDial,
        },
        props: {
          default: true,
        },
        children: [
          {
            path: "",
            redirect: "dashboard",
          },
          {
            path: "dashboard",
            name: "OrganizationDashboard",
            component: OrganizationDashboard,
            props: (route) => ({
              orgId: route.params.id,
            }),
            children: [
              {
                path: "",
                redirect: "organization_summary",
              },
              {
                path: "organization_summary",
                name: "OrganizationSummary",
                component: OrganizationSummary,
                props: (route) => ({
                  orgId: route.params.id,
                }),
              },
              {
                path: "assign_tokens",
                name: "OrganizationAssignTokens",
                component: OrganizationAssignTokens,
              },
              {
                path: "schedule_device_provisioning",
                name: "OrganizationScheduleDeviceProvisioning",
                component: OrganizationScheduleDeviceProvisioning,
              },
              {
                path: "join_chart",
                name: "OrganizationJoinChart",
                component: OrganizationJoinChart,
                props: (route) => ({
                  orgId: route.params.id,
                }),
              },
              {
                path: "packets_chart",
                name: "OrganizationPacketsChart",
                component: OrganizationPacketsChart,
                props: (route) => ({
                  orgId: route.params.id,
                }),
              },
              {
                path: "crc_errors_chart",
                name: "OrganizationCrcErrorsChart",
                component: OrganizationCrcErrorsChart,
                props: (route) => ({
                  orgId: route.params.id,
                }),
              },
              {
                path: "missed_packets_chart",
                name: "OrganizationMissedPacketsChart",
                component: OrganizationMissedPacketsChart,
                props: (route) => ({
                  orgId: route.params.id,
                }),
              },
              {
                path: "join_requests",
                name: "OrganizationJoinRequests",
                component: JoinRequests,
                props: (route) => ({
                  orgId: route.params.id,
                }),
              },
              {
                path: "simple_join_requests",
                name: "SimpleJoinRequests",
                component: SimpleJoinRequests,
                props: (route) => ({
                  orgId: route.params.id,
                }),
              },
              {
                path: "packets",
                name: "OrganizationPackets",
                component: Packets,
                props: (route) => ({
                  orgId: route.params.id,
                }),
              },
              {
                path: "network_statistics",
                name: "OrganizationNetworkStatistics",
                component: NetworkStatistics,
                props: (route) => ({
                  orgId: route.params.id,
                }),
              },
              {
                path: "gateway_statistics",
                name: "OrganizationGatewayStatistics",
                component: GatewayStatistics,
                props: (route) => ({
                  orgId: route.params.id,
                }),
              },
            ],
          },
          {
            path: "gateways",
            name: "OrganizationGateways",
            component: GatewaysTable,
            props: (route) => ({
              orgId: route.params.id,
            }),
            children: [
              {
                path: "new",
                name: "OrganizationGatewaysNew",
                props: true,
                components: {
                  orgModal: NewGatewayModal,
                },
              },
            ],
          },
          {
            path: "end_devices",
            name: "OrganizationEndDevices",
            component: EndDevicesTable,
            props: (route) => ({
              orgId: route.params.id,
            }),
            children: [
              {
                path: "new",
                name: "OrganizationEndDevicesNew",
                props: true,
                components: {
                  orgModal: NewEndDeviceModal,
                },
              },
            ],
          },
          {
            path: "app_networks",
            name: "OrganizationAppNetworks",
            component: ApplicationNetworksList,
            props: (route) => ({
              orgId: route.params.id,
            }),
            children: [
              {
                path: "new",
                name: "AppNetworksNew",
                props: true,
                components: {
                  orgModal: NewApplicationNetworkModal,
                },
              },
            ],
          },
          {
            path: "users",
            name: "OrganizationUsers",
            component: Users,
            props: (route) => ({
              id: route.params.userId,
              orgId: route.params.id,
            }),
            children: [
              {
                path: "new",
                name: "OrganizationUsersNew",
                props: true,
                components: {
                  default: UserModal,
                },
              },
              {
                path: ":userId/edit",
                name: "OrganizationUserEdit",
                components: {
                  default: UserModal,
                },
              },
            ],
          },
          {
            path: "end-devices/profiles",
            name: "OrganizationDeviceProfiles",
            component: OrganizationDeviceProfiles,
            props: true,
            children: [
              {
                path: ":deviceProfileId",
                name: "OrganizationDeviceProfileEdit",
                components: {
                  default: DeviceProfileModal,
                },
              },
            ],
          },
          {
            path: "sessions",
            name: "OrganizationSessions",
            component: OrganizationSessions,
            props: true,
          },
          {
            path: "create_revisions",
            name: "OrganizationCreateRevisions",
            component: OrganizationCreateRevisions,
            props: true,
          },
          {
            path: "update_revisions",
            name: "OrganizationUpdateRevisions",
            component: OrganizationUpdateRevisions,
            props: true,
          },
          {
            path: "destroy_revisions",
            name: "OrganizationDestroyRevisions",
            component: OrganizationDestroyRevisions,
            props: true,
          },
        ],
      },
      {
        path: "gateways",
        name: Gateways.name,
        components: {
          default: Gateways,
          speeddial: GatewaySpeedDial,
        },
        props: {
          speeddial: (route) => ({
            routeName: route.name,
            routeKey: route.path.split("/")[1],
          }),
        },
        children: [
          {
            path: "new",
            name: "GatewaysNew",
            component: NewGatewayModal,
          },
          {
            path: "upload",
            name: "GatewaysUpload",
            component: UploadGatewayModal,
          },
        ],
      },
      {
        path: "gateways/:eui",
        components: {
          default: Gateway,
          speeddial: GatewaySpeedDial,
        },
        props: {
          default: true,
        },
        children: [
          {
            path: "",
            redirect: "dashboard",
          },
          {
            path: "dashboard",
            name: "GatewayDashboard",
            component: GatewayDashboard,
            props: true,
          },
          {
            path: "join-requests",
            name: "GatewayJoinRequests",
            component: JoinRequestsTable,
            props: (route) => ({
              gatewayEui: route.params.eui,
              hidden: ["gw_eui"],
            }),
          },
          {
            path: "device-states",
            name: "LocalGatewayDeviceStates",
            component: GatewayDeviceStates,
          },
          {
            path: "packets",
            name: "GatewayPackets",
            component: PacketsTable,
            props: (route) => ({
              gatewayEui: route.params.eui,
              uplink: true,
              downlink: true,
              hidden: ["gateway_eui"],
            }),
          },
          {
            path: "statistics",
            name: "GatewayStatistics",
            component: GatewayStatisticsTable,
            props: true,
          },
          {
            path: "network-statistics",
            name: "NetworkStatistics",
            component: NetworkStatisticsTable,
            props: true,
          },
          {
            path: "details",
            name: "GatewayDetails",
            component: GatewayDetails,
            props: true,
          },
          {
            path: "revisions",
            name: "GatewayRevisions",
            component: GatewayRevisions,
            props: true,
          },
          {
            path: "rssi-spectral-scan",
            name: "RssiSpectralScan",
            component: RssiSpectralScan,
            props: true,
          },
          {
            path: "gateway-license",
            name: "GatewayLicense",
            component: GatewayLicense,
            props: true,
          },
          {
            path: "(.*)?new",
            name: "GatewayNew",
            components: { modal: NewGatewayModal },
          },
          {
            path: "(.*)?upload",
            name: "GatewayUpload",
            components: { modal: UploadGatewayModal },
          },
        ],
      },
      {
        path: "end-devices/profiles",
        name: DeviceProfiles.name,
        components: {
          default: DeviceProfiles,
          speeddial: DeviceProfileSpeedDial,
        },
        props: {
          speeddial: (route) => ({
            routeName: route.name,
            routeKey: route.path.split("/")[1],
          }),
        },
        children: [
          {
            path: "new",
            name: "DeviceProfilesNew",
            component: DeviceProfileModal,
          },
        ],
      },
      {
        path: "end-devices/profiles/:id",
        components: {
          default: DeviceProfile,
          speeddial: DeviceProfileSpeedDial,
        },
        props: {
          default: true,
        },
        children: [
          {
            path: "(.*)?new",
            name: "DeviceProfileNew",
            components: { modal: DeviceProfileModal },
          },
          {
            path: "",
            redirect: "revisions",
          },
          {
            path: "revisions",
            name: "DeviceProfileRevisions",
            component: DeviceProfileRevisions,
            props: true,
          },
        ],
      },
      {
        path: "policies",
        name: Policies.name,
        components: {
          default: Policies,
          speeddial: PolicySpeedDial,
        },
        props: {
          speeddial: (route) => ({
            routeName: route.name,
            routeKey: "/policies/new",
          }),
        },
        children: [
          {
            path: "new",
            name: "PoliciesNew",
            component: NewPolicyModal,
          },
        ],
      },
      {
        path: "policies/:id",
        name: Policy.name,
        components: {
          default: Policy,
          speeddial: PolicySpeedDial,
        },
        props: {
          default: true,
          speeddial: (route) => ({
            routeName: route.name,
            routeKey: "/policies/new",
          }),
        },
        children: [
          {
            path: "(.*)?new",
            name: "PolicyNew",
            components: { modal: NewPolicyModal },
          },
        ],
      },
      {
        path: "people",
        name: Users.name,
        components: {
          default: Users,
          speeddial: UserSpeedDial,
        },
        children: [
          {
            path: "new",
            name: "UserNew",
            component: UserModal,
            props: { creator: true, newUser: true },
          },
          {
            path: ":id",
            name: "UserEdit",
            component: UserModal,
            props: true,
          },
        ],
      },
      {
        path: "people/:id/actions",
        name: "UserActions",
        component: UserActions,
        props: true,
      },
      {
        path: "people/:id/revisions",
        name: "UserRevisions",
        component: UserRevisions,
        props: true,
      },
      {
        path: "me",
        name: Me.name,
        component: Me,
      },
      {
        path: "support",
        name: Support.name,
        component: Support,
      },
      {
        path: "operations",
        name: Operations.name,
        component: Operations,
      },
      {
        path: "end-devices/groups",
        name: DeviceGroups.name,
        components: {
          default: DeviceGroups,
          speeddial: DeviceGroupSpeedDial,
        },
        children: [
          {
            path: "new",
            name: "NewDeviceGroup",
            component: NewDeviceGroupModal,
          },
        ],
      },
      {
        path: "end-devices/groups/:id",
        components: {
          default: DeviceGroup,
          speeddial: DeviceGroupSpeedDial,
        },
        props: {
          default: true,
        },
        children: [
          {
            path: "",
            redirect: "devices",
          },
          {
            path: "devices",
            name: "DeviceGroupEndDevices",
            component: EndDevicesTable,
            props: (route) => ({
              deviceGroupEui: route.params.id || route.params.eui,
              showGroupAddRemoveControls: true,
              suppressBulkEdit: true,
            }),
          },
          {
            path: "add-devices",
            name: "DeviceGroupAddEndDevices",
            component: EndDevicesTable,
            props: (route) => ({
              deviceGroupEui: route.params.id || route.params.eui,
              excludeDeviceGroup: true,
              showGroupAddRemoveControls: true,
              suppressBulkEdit: true,
            }),
          },
        ],
      },
      {
        path: "operations/new-message",
        name: "NewOperationMessage",
        component: NewOperation,
        props: {
          type: "message",
        },
      },
      {
        path: "operations/firmware-upgrade",
        name: "NewFirmwareUpgrade",
        component: NewOperation,
        props: {
          type: "firmware",
        },
      },
      {
        path: "operations/:id/",
        name: "Operation",
        component: Operation,
        props: true,
      },
      {
        path: "end-devices",
        name: EndDevices.name,
        components: {
          default: EndDevices,
          speeddial: EndDeviceSpeedDial,
        },
        props: {
          speeddial: (route) => ({
            routeName: route.name,
            routeKey: route.path.split("/")[1],
          }),
        },
        children: [
          {
            path: "new",
            name: "EndDevicesNew",
            component: NewEndDeviceModal,
          },
          {
            path: "upload",
            name: "EndDevicesUpload",
            component: UploadEndDeviceModal,
          },
        ],
      },
      {
        path: "end-devices/:eui",
        components: {
          default: EndDevice,
          speeddial: EndDeviceSpeedDial,
        },
        props: {
          default: true,
        },
        children: [
          {
            path: "",
            redirect: "dashboard",
          },
          {
            path: "dashboard",
            name: "EndDeviceDashboard",
            component: EndDeviceDashboard,
            props: true,
          },
          {
            path: "join-requests",
            name: "EndDeviceJoinRequests",
            component: JoinRequestsTable,
            props: (route) => ({
              endDeviceEui: route.params.eui,
              hidden: ["dev_eui"],
            }),
          },
          {
            path: "packets",
            name: "EndDevicePackets",
            component: PacketsTable,
            props: (route) => ({
              endDeviceEui: route.params.eui,
              uplink: true,
              downlink: true,
              hidden: ["end_device_eui"],
            }),
          },
          {
            path: "revisions",
            name: "EndDeviceRevisions",
            component: EndDeviceRevisions,
            props: true,
          },
          {
            path: "(.*)?new",
            name: "EndDeviceNew",
            components: { modal: NewEndDeviceModal },
          },
          {
            path: "(.*)?upload",
            name: "EndDeviceUpload",
            components: { modal: UploadEndDeviceModal },
          },
        ],
      },
      {
        path: "systemlimits",
        name: "SettingsSystemLimits",
        component: SystemLimits,
      },
      {
        path: "vaults",
        name: "Vaults",
        component: Vaults,
        children:[
          {
          name: "VaultTransactions",
            path: 'transactions',
            component: VaultTransactions
          }
        ]
      },
      {
        path: "vaults/new",
        name: "VaultCreateController",
        component: VaultCreateController,
      },
      {
        path: "vaults/:id/update",
        name: "VaultUpdateController",
        component: VaultUpdateController,
        props: true,
        children:[
          {
            path: "site_create",
            name: "LensSiteCreateView",
            component: LensSiteCreateView,
            props: true,
          },
          {
            path: "site_update/:site_id",
            name: "LensSiteUpdateView",
            component: LensSiteUpdateView,
            props: true,
            children: [
              {
                path: "org_create",
                name: "TenantAccountCreateView",
                component: TenantAccountCreateView,
                props: true,
              },
              {
                path: "org_update/:tenant_account_id",
                name: "TenantAccountUpdateView",
                component: TenantAccountUpdateView,
                props: true,
              },
            ]
          },
        ]
      },
      {
        path: "systembroadcast",
        name: "SystemBroadcast",
        component: SystemBroadcast,
      },
      {
        path: 'service-api-keys',
        name: 'ServiceApiKeys',
        components: { 
          default: ServiceApiKeys, 
          speeddial: ServiceApiKeySpeedDial 
        },
        props: route => ({
          id: route.params.id,
        }),
        children: [
          {
            path: 'new',
            name: 'NewServiceApiKeyModal',
            props: {
              speeddial: route => ({
                routeName: route.name,
                routeKey: route.path.split('/')[1],
              }),
            },
            components: {
              orgModal: NewServiceApiKeyModal
            },
          },
          {
            path: ':id',
            name: 'EditServiceApiKeyModal',
            props: true,
            components: {
              orgModal: NewServiceApiKeyModal
            },
          },
        ],
      },
    ],
  },
];

const router = new Router({ routes });

const resolveTopLevelRouteComponents = async () => {
  // Wait a bit to let everything render and make API calls
  await delay(3500);

  const AppRoute = routes.find((route) => route.path === "/");

  if (!AppRoute) {
    throw new Error("[router.js] The AppRoute cannot be found!");
  }

  // $FlowFixMe
  const topLevelRouteComponets = AppRoute.children
    .filter(
      (route) => route.component || oPath.get(route, "components.default")
    )
    .map(
      (route) =>
        route.component || (route.components && route.components.default)
    );

  // Resolve all the top level route components so they will be ready to use
  for (const component of topLevelRouteComponets) {
    if (typeof component === "function") {
      // Wait until this component is resolved before going to the next one
      await component();
    }
  }
  // Also resolve these since they are large assets
  import(/* webpackChunkName: "EntityForm" */ "@/components/EntityForm");
  import(/* webpackChunkName: "DataTable" */ "@/components/DataTable");
  import(/* webpackChunkName: "UserForm" */ "@/components/UserForm");
};

// Called when the router has completed the initial navigation, which means it has resolved
// all async enter hooks and async components that are associated with the initial route.
router.onReady(resolveTopLevelRouteComponents);

const simpleSubRouteChange = (to, from) =>
  from.matched.length === to.matched.length &&
  to.matched.length > 2 &&
  equal(from.params, to.params);

const isMajorRouteChange = (to, from) => !simpleSubRouteChange(to, from);

router.beforeEach((to, from, next) => {
  // Don't run for route change like /gateways/:eui/packets to /gateways/:eui/statistics
  if (isMajorRouteChange(to, from)) {
    if (store.state.unsavedChanges) {
      if (!window.confirm("You have unsaved changes! They will be discarded")) {
        next(false);
        return;
      }
      store.commit(SET_UNSAVED_CHANGES_FALSE);
    }

    window.scrollTo(0, 0);
  }

  if (to.matched.every((record) => record.meta.noAuth)) {
    // Route doesn't require auth
    return next();
  } else if (
    !store.state.session.authToken ||
    isPast(store.state.session.expiresAt)
  ) {
    // Route requires auth
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.log("Router requires auth, redirecting...");
    }
    return next({
      path: "/login",
      query: { redirect: to.fullPath },
    });
  } else if (
    to.matched.some((record) => record.meta.adminOnly) &&
    store.getters.currentUser.roles_mask > 4
  ) {
    // Only admins can access these routes, so redirect to dashboard
    return next({
      path: "/dashboard",
    });
  }
  if (
    store.state.session.user.eula_accepted_rev !=
      store.state.session.user.eula_current_rev &&
    to.path.includes("/eula") === false
  ) {
    // If you are already at eula, don't keep going to eula otherwise you will get a recurrsion problem
    next("/eula");
  } else {
    next();
  }
});

router.afterEach((to, from) => {
  if (process.env.NODE_ENV === "production") {
    if (
      process.env.GIT_NEW_FRONTEND_VERSION !== GIT_VERSION ||
      (window.serviceWorkerUpdateIsReady && isMajorRouteChange(to, from))
    ) {
      // This will cause the new service worker to take over.
      // Old service worker cache will be deleted
      // When the onUpdated event fires, we force a page reload to use the new assets
      OfflinePluginRuntime.applyUpdate();
    }
  }
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js